// src/components/FlowMap.js
import React, { useMemo, useRef, useEffect, useState } from 'react';
import Dropdown from './Dropdown';
import ContextMenu from './ContextMenu';
import * as topojson from 'topojson-client';

// --- Helper Functions (outside component) ---

const projectPoint = (lng, lat, width, height) => {
  const x = ((lng + 180) / 360) * width;
  const y = ((90 - lat) / 180) * height;
  return { x, y };
};

// Function to check distance from a point to a quadratic bezier curve
const isPointNearQuadraticBezier = (p, p0, p1, p2, threshold) => {
    let minDistance = Infinity;
    for (let t = 0; t <= 1; t += 0.05) {
        const x = (1 - t) * (1 - t) * p0.x + 2 * (1 - t) * t * p1.x + t * t * p2.x;
        const y = (1 - t) * (1 - t) * p0.y + 2 * (1 - t) * t * p1.y + t * t * p2.y;
        const dist = Math.sqrt(Math.pow(p.x - x, 2) + Math.pow(p.y - y, 2));
        if (dist < minDistance) {
            minDistance = dist;
        }
    }
    return minDistance < threshold;
};

const drawWorldMap = (ctx, width, height, geoJson) => {
  ctx.strokeStyle = '#555'; // Brighter stroke
  ctx.fillStyle = '#333';   // Brighter fill
  ctx.lineWidth = 0.7;

  geoJson.features.forEach(feature => {
    const { type, coordinates } = feature.geometry;
    const polygons = type === 'MultiPolygon' ? coordinates : [coordinates];

    polygons.forEach(polygon => {
      polygon.forEach(ring => {
        if (ring.length < 2) return;
        ctx.beginPath();
        const startPoint = projectPoint(ring[0][0], ring[0][1], width, height);
        ctx.moveTo(startPoint.x, startPoint.y);
        for (let i = 1; i < ring.length; i++) {
          const point = projectPoint(ring[i][0], ring[i][1], width, height);
          ctx.lineTo(point.x, point.y);
        }
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
      });
    });
  });
};

const drawFlows = (ctx, flows, width, height, geometries) => {
  geometries.points = [];
  geometries.lines = [];
  
  const flowMap = new Map();
  flows.forEach(flow => {
    if (!flow.sourceObject?.location || !flow.targetObject?.location) return;
    const key = `${flow.source}-${flow.target}`;
    const existing = flowMap.get(key) || { value: 0, flows: [] };
    existing.value += flow.value || 0;
    existing.flows.push(flow);
    flowMap.set(key, existing);
  });

  const allValues = Array.from(flowMap.values()).map(f => f.value);
  const maxValue = Math.max(...allValues);

  Array.from(flowMap.values()).forEach(({ value, flows }) => {
    const representativeFlow = flows[0]; // Use first flow for geo data
    const { sourceObject, targetObject, sourceStage, targetStage } = representativeFlow;

    const sourcePoint = projectPoint(sourceObject.location.coordinates[0], sourceObject.location.coordinates[1], width, height);
    const targetPoint = projectPoint(targetObject.location.coordinates[0], targetObject.location.coordinates[1], width, height);
    
    geometries.points.push({ ...sourcePoint, data: sourceObject });
    geometries.points.push({ ...targetPoint, data: targetObject });

    const lineWidth = Math.max(0.8, (value / maxValue) * 6);
    let color = '#ff7a21'; // default
    if (sourceStage === 'Mine') color = '#ffd166';
    if (targetStage === 'Primary') color = '#3E8899';

    const midX = (sourcePoint.x + targetPoint.x) / 2 + (targetPoint.y - sourcePoint.y) * 0.15;
    const midY = (sourcePoint.y + targetPoint.y) / 2 - (targetPoint.x - sourcePoint.x) * 0.15;
    
    geometries.lines.push({ p0: sourcePoint, p1: {x: midX, y: midY}, p2: targetPoint, data: { flows, value }});
    
    // Drawing logic
    ctx.beginPath();
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.globalAlpha = 0.7;
    ctx.moveTo(sourcePoint.x, sourcePoint.y);
    ctx.quadraticCurveTo(midX, midY, targetPoint.x, targetPoint.y);
    ctx.stroke();

    ctx.globalAlpha = 1;
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(sourcePoint.x, sourcePoint.y, 4, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(targetPoint.x, targetPoint.y, 4, 0, Math.PI * 2);
    ctx.fill();
  });
};

// --- React Component ---

export default function FlowMap({ flows }) {
  const canvasRef = useRef(null);
  const geometriesRef = useRef({ points: [], lines: [] });
  const [contextMenu, setContextMenu] = useState(null);
  const [geoJson, setGeoJson] = useState(null);
  const [hoverInfo, setHoverInfo] = useState(null);
  const [stageFilter, setStageFilter] = useState('All');

  const stageOptions = useMemo(() => {
    const stages = new Set();
    flows.forEach(flow => {
      if (flow.sourceStage && flow.targetStage) {
        stages.add(`${flow.sourceStage} → ${flow.targetStage}`);
      }
    });
    return ['All', ...Array.from(stages).sort()];
  }, [flows]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('https://cdn.jsdelivr.net/npm/world-atlas@2/countries-50m.json');
        if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        const topo = await res.json();
        setGeoJson(topojson.feature(topo, topo.objects.countries));
      } catch (error) { console.error('FlowMap: Failed to load world map data:', error); }
    })();
  }, []);

  useEffect(() => {
    if (!geoJson) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    ctx.fillStyle = '#0b0b0b';
    ctx.fillRect(0, 0, rect.width, rect.height);

    drawWorldMap(ctx, rect.width, rect.height, geoJson);
    const currentFlows = stageFilter === 'All' ? flows : flows.filter(f => {
      const flowStage = `${f.sourceStage} → ${f.targetStage}`;
      return flowStage === stageFilter;
    });
    drawFlows(ctx, currentFlows, rect.width, rect.height, geometriesRef.current);
  }, [flows, geoJson, stageFilter]);

  const handleMouseMove = (event) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    // Check points first
    for (const point of geometriesRef.current.points) {
        const dist = Math.sqrt(Math.pow(point.x - x, 2) + Math.pow(point.y - y, 2));
        if (dist < 8) { // 8px hover radius
            setHoverInfo({ x: event.clientX, y: event.clientY, type: 'point', data: point.data });
            return;
        }
    }
    
    // Check lines
    for (const line of geometriesRef.current.lines) {
        if (isPointNearQuadraticBezier({x, y}, line.p0, line.p1, line.p2, 8)) {
            setHoverInfo({ x: event.clientX, y: event.clientY, type: 'line', data: line.data });
            return;
        }
    }

    setHoverInfo(null);
  };

  const handleMouseLeave = () => {
    setHoverInfo(null);
  };
  
  // (ContextMenu handler is unchanged)
  const handleContextMenu = (e) => {
    e.preventDefault();
    if (!flows?.length) return;

    const downloadData = () => {
      const csv = flows.map(f => [
        f.source, f.target, f.value, f.year, f.sourceStage, f.targetStage
      ].join(',')).join('\n');
      const header = 'Source,Target,Value,Year,Source Stage,Target Stage\n';
      const blob = new Blob([header + csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'tungsten-flows.csv';
      a.click();
    };

    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      options: [
        { label: 'Download flow data as CSV', action: downloadData }
      ]
    });
  };

  return (
    <div className="panel map" onContextMenu={handleContextMenu}>
      <div className="panel-header">
        <div className="panel-title">
          <h3>Material Flows</h3>
          <img src="/assets/rovjok_logo.png" alt="Rovjok Logo" className="header-logo" />
        </div>
        <div className="control control-row">
          <label>Stage</label>
          <Dropdown value={stageFilter} onChange={setStageFilter} options={stageOptions} />
        </div>
      </div>
      <canvas 
        ref={canvasRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{ width: '100%', height: '400px', display: 'block', cursor: hoverInfo ? 'pointer' : 'default' }}
      />
      {hoverInfo && (
        <div className="map-infobox" style={{ left: `${hoverInfo.x + 15}px`, top: `${hoverInfo.y + 15}px` }}>
          {hoverInfo.type === 'point' && (
            <>
              <div className="title">{hoverInfo.data.name}</div>
              <div><strong>Owner:</strong> {hoverInfo.data.owner}</div>
              <div><strong>Type:</strong> {hoverInfo.data.type}</div>
            </>
          )}
          {hoverInfo.type === 'line' && (
            <>
              <div className="title">
                {hoverInfo.data.flows[0].source} → {hoverInfo.data.flows[0].target}
              </div>
              <div><strong>Total Value:</strong> {hoverInfo.data.value.toLocaleString(undefined, {maximumFractionDigits: 2})}</div>
              <div><strong>Connections:</strong> {hoverInfo.data.flows.length}</div>
            </>
          )}
        </div>
      )}
      <div className="legend">
        <span className="badge"><span className="swatch" style={{backgroundColor: '#ffd166'}}></span> Mine to Processing</span>
        <span className="badge"><span className="swatch" style={{backgroundColor: '#3E8989'}}></span> Processing to Primary</span>
        <span className="badge"><span className="swatch" style={{backgroundColor: '#ff7a21'}}></span> Other Flows</span>
      </div>
      {contextMenu && <ContextMenu {...contextMenu} onRequestClose={() => setContextMenu(null)} />}
    </div>
  );
}